import {
  CHART_EMPTY_KEY,
  CHART_OTHER_KEY,
  ChartCategory,
  ChartData,
  ChartLabels,
  ChartSeries,
  ChartSpec,
  ChartType
} from './chart-spec.interfaces';

const MAX_LABEL_LENGTH = 20;
const ROTATED_LABELS_THRESHOLD = 8;
const ROTATED_LABEL_ANGLE = 30;
const ELLIPSIS = '…';
const PIE_RADIUS = '65%';
const DONUT_RADIUS = ['40%', '70%'];
const HISTOGRAM_BAR_GAP = '2%';
const STACK_NAME = 'total';
const SHARE_LABEL_FORMAT = '{b} {d}%';
const RESERVED_KEYS = [CHART_OTHER_KEY, CHART_EMPTY_KEY];

const SHARE_CHARTS = [ChartType.PIE, ChartType.DONUT];
const TIME_CHARTS = [ChartType.LINE, ChartType.AREA];

/**
 * Pure mapping from a chart specification and its sliced figures to an ECharts option; returns null for KPI charts,
 * which are rendered as cards. "Other" and "(empty)" members are drawn in the neutral color when one is given.
 */
export function buildChartOption(spec: ChartSpec, data: ChartData, labels: ChartLabels, palette: string[],
                                 neutralColor?: string): Record<string, unknown> | null {
  if (spec.chartType === ChartType.KPI) {
    return null;
  }
  const isShare = SHARE_CHARTS.includes(spec.chartType);
  const categories = data.categories.map((category: ChartCategory) => categoryLabel(category, labels));
  const base = {
    color: palette,
    tooltip: {trigger: isShare ? 'item' : 'axis'},
    legend: {show: !isShare && data.series.length > 1, type: 'scroll', bottom: 0}
  };
  if (isShare) {
    return {...base, series: [shareSeries(spec, data, categories, labels, neutralColor)]};
  }
  const categoryAxis = {type: 'category', data: categories, axisLabel: categoryAxisLabel(categories)};
  const valueAxis = {type: 'value'};
  const horizontal = spec.chartType === ChartType.HORIZONTAL_BAR;
  return {
    ...base,
    grid: {containLabel: true, left: 16, right: 16, top: 16, bottom: 40},
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? {...categoryAxis, inverse: true} : valueAxis,
    series: data.series.map((series: ChartSeries) => cartesianSeries(spec, series, data, labels, neutralColor))
  };
}

export function categoryLabel(category: { key: string, label?: string }, labels: ChartLabels): string {
  if (category.key === CHART_OTHER_KEY) {
    return labels.other;
  }
  if (category.key === CHART_EMPTY_KEY) {
    return labels.empty;
  }
  return category.label || category.key;
}

function seriesName(series: ChartSeries, labels: ChartLabels): string {
  return series.label || RESERVED_KEYS.includes(series.key) ? categoryLabel(series, labels) : labels.count;
}

function shareSeries(spec: ChartSpec, data: ChartData, categories: string[], labels: ChartLabels,
                     neutralColor?: string): Record<string, unknown> {
  const values = data.series[0]?.values ?? [];
  return {
    type: 'pie',
    name: data.series[0] ? seriesName(data.series[0], labels) : labels.count,
    radius: spec.chartType === ChartType.DONUT ? DONUT_RADIUS : PIE_RADIUS,
    label: {formatter: SHARE_LABEL_FORMAT},
    data: categories.map((name: string, index: number) => {
      const item = {name, value: values[index] ?? 0};
      return isNeutral(data.categories[index].key, neutralColor) ? {...item, itemStyle: {color: neutralColor}} : item;
    })
  };
}

function cartesianSeries(spec: ChartSpec, series: ChartSeries, data: ChartData, labels: ChartLabels,
                         neutralColor?: string): Record<string, unknown> {
  const isTimeChart = TIME_CHARTS.includes(spec.chartType);
  const isNeutralSeries = data.series.length > 1 && isNeutral(series.key, neutralColor);
  return {
    type: isTimeChart ? 'line' : 'bar',
    name: seriesName(series, labels),
    data: isTimeChart || data.series.length > 1
      ? series.values
      : series.values.map((value: number | null, index: number) =>
        isNeutral(data.categories[index]?.key, neutralColor) ? {value, itemStyle: {color: neutralColor}} : value),
    ...(isNeutralSeries ? {itemStyle: {color: neutralColor}} : {}),
    ...(spec.chartType === ChartType.AREA ? {areaStyle: {}} : {}),
    ...(spec.chartType === ChartType.STACKED_BAR ? {stack: STACK_NAME} : {}),
    ...(spec.chartType === ChartType.HISTOGRAM ? {barCategoryGap: HISTOGRAM_BAR_GAP} : {})
  };
}

function isNeutral(key: string | undefined, neutralColor?: string): boolean {
  return !!neutralColor && !!key && RESERVED_KEYS.includes(key);
}

function categoryAxisLabel(categories: string[]): Record<string, unknown> {
  return {
    interval: 0,
    rotate: categories.length > ROTATED_LABELS_THRESHOLD ? ROTATED_LABEL_ANGLE : 0,
    formatter: truncateLabel
  };
}

export function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_LENGTH ? label.substring(0, MAX_LABEL_LENGTH - 1) + ELLIPSIS : label;
}
